import { GraphDto } from "@custom-types/graphs"
import { PageMode } from "@features/builder/GraphBuilderPage"
import ActionsDropdownButton, {
  ActionsDropdownButtonProps,
} from "@features/builder/TopActionBar/ActionsDropdownButton"
import ConfigurationDropdownButton, {
  ConfigurationDropdownButtonProps,
} from "@features/builder/TopActionBar/ConfigurationDropdownButton"
import { Searchbutton } from "@features/builder/TopActionBar/SearchButton"
import SubscribersIndicator from "@features/builder/TopActionBar/SubscribersIndicator"
import { Grid, Tooltip, Typography, tooltipClasses } from "@mui/material"
import React from "react"
import { InfoOutlined } from "@mui/icons-material"
import { useGraphDocument } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocument"
import { GraphDocument } from "@features/builder/types"

type TopActionBarProps = {
  mode: PageMode
  graph: GraphDto | undefined
  onSampleClick: (doc?: GraphDocument) => void
  onPublishInternallyClick: (doc?: GraphDocument) => void
} & Partial<ActionsDropdownButtonProps> &
  Partial<ConfigurationDropdownButtonProps>

const TopActionBar = (props: TopActionBarProps) => {
  const graphDocument = useGraphDocument()

  if (props.mode === PageMode.Start) {
    return <></>
  }

  const graphTitle =
    props.mode === PageMode.Edit && props.graph ? `${props.graph.name}` : "Unsaved graph"
  const isPrivateGraph = props.graph?.owner_group_key === "private"
  const graphVersion = props.graph ? props.graph.version : "0.0.0"

  return (
    <Grid container wrap="nowrap" gap={1}>
      {/* top row */}
      <Grid container item direction="column" gap={1}>
        <Grid container item justifyContent="space-between">
          <Grid item data-testid="graph-name-and-version-header">
            {isPrivateGraph && (
              <Typography
                variant="h5"
                component="span"
                sx={{
                  borderRadius: "5px",
                  px: "5px",
                  py: 0,
                  backgroundColor: "rgba(33, 150, 243, 0.2)",
                }}
              >
                Private
              </Typography>
            )}
            <Typography variant="h5" component="span">
              <strong> {graphTitle}</strong> {graphVersion}
              <Tooltip
                PopperProps={{
                  sx: { [`& .${tooltipClasses.tooltip}`]: { maxWidth: "none" } },
                }}
                title={
                  <ul style={{ paddingLeft: 20 }}>
                    <li>
                      Graph version is only a reference to indicate what is the latest version of
                      it.
                    </li>
                    <li>
                      The graph you see now is live version built on top of the latest version.
                    </li>
                    <li>Graph version is changed on every sampling or by publishing it.</li>
                  </ul>
                }
              >
                <InfoOutlined sx={{ ml: 1, verticalAlign: "text-top" }} />
              </Tooltip>
            </Typography>
          </Grid>
          <Grid item>
            <SubscribersIndicator graphId={props.graph?.key} />
          </Grid>
        </Grid>

        {/* bottom row */}
        <Grid container gap={1} wrap="nowrap" justifyContent="space-between">
          {/* left side */}
          <Grid container item xs="auto" gap={1}>
            <Grid item>
              <Searchbutton />
            </Grid>
          </Grid>

          {/* right side */}
          <Grid container item xs="auto" gap={1}>
            <Grid item>
              <ActionsDropdownButton
                isPrivate={props.graph?.owner_group_key === "private"}
                onDeleteClick={props.onDeleteClick}
                onExposeClick={props.onExposeClick}
                onSampleClick={() => props.onSampleClick(graphDocument)}
                onPublishInternallyClick={() => props.onPublishInternallyClick(graphDocument)}
                onSaveSnapshotClick={props.onSaveSnapshotClick}
                onRestoreSnapshotClick={props.onRestoreSnapshotClick}
                onBulkNodesEditClick={props.onBulkNodesEditClick}
                onExportGraphClick={props.onExportGraphClick}
                onImportGraphClick={props.onImportGraphClick}
              />
            </Grid>
            <Grid item>
              <ConfigurationDropdownButton
                onEditMetadataClick={props.onEditMetadataClick}
                onAddPostSamplingValidationClick={props.onAddPostSamplingValidationClick}
                onAddDashboardConfigurationClick={props.onAddDashboardConfigurationClick}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default TopActionBar
